import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  keywordType,
  keywordGroupType,
  competitorsType,
  addDelKeywordParamsType,
  IApartAddDelKeywordParams,
  IApartAddAllKeywordParams,
  bpartTabType,
  IBpartTrackedColorParams,
  IBpartTrackedAlarmParams,
  IBpartTrackedDeleteParams,
  IBpartTrackedLiveSuggestParams,
  IBpartTrackedLivePopupData,
  IBpartTrackedSuggestPopupData,
  IBpartTrackedSuggestPopupAddDelParams,
  IBpartKeywordDelParams,
} from '@models/keyword/keywordModel';
import { reduxThunkStatus } from '@configs/restApi';
import { keyword_Bpart_Tab_Arr, popup_Category } from '@models/keyword/keyword';

import {
  fetchApartKeywordDataList,
  fetchBpartKeywordDataList,
  fetchApartReqAddDelKeyword,
  fetchApartReqAddAllKeyword,
  fetchBpartReqColorSelect,
  fetchBpartReqAlarm,
  fetchBpartReqDelete,
  fetchBpartReqLivePopup,
  fetchBpartReqSuggestPopup,
  fetchBpartSuggestPopupAddDel,
  fetchBpartReqKeywordDelete,
} from './thunk/trackingThunk';

const initialState = {
  apartkeywordList: null as any, // ApartKeywordData
  bpartKeywordList: null as any, // BpartKeywordData
  requestStatusBpartKeywordList: '' as string, // requestStatus of BpartKeywordData
  suggestPopupData: null as IBpartTrackedSuggestPopupData, // suggestPopupData
  suggestPopupKeyword: null as string, // suggestPopup 팝업 뜰때 기준 keyword
  livePopupData: null as IBpartTrackedLivePopupData, // livePopupData
  currentBpartKeywordList: null as string[], // 현재 BpartKeywordList for keywordId arr
  currentBpartTabType: keyword_Bpart_Tab_Arr[0].value as bpartTabType,
};

export const keywordTrackingSlice = createSlice({
  name: 'keywordTracking',
  initialState: initialState,
  reducers: {
    addRequestKeywordList: (state, { payload: { keyword } }) => {
      state.currentBpartKeywordList = state.currentBpartKeywordList.concat(keyword);
    },
    delRequestKeywordList: (state, { payload: { keyword } }) => {
      state.currentBpartKeywordList = state.currentBpartKeywordList.filter((item) => item !== keyword);
    },
    deleteBpartKeywordList: (state, { payload: { keywordId } }) => {
      state.bpartKeywordList = state.bpartKeywordList.filter((item) => item.keywordId !== keywordId);
    },
    // 현재 선택한 bpartTab
    setCurrentBpartTabType: (state, { payload: { bpartTab } }) => {
      state.currentBpartTabType = bpartTab;
    },
    // Bpart Tracked 컬러 팝업 열고닫기
    colorPopupHandler: (state, { payload }: { payload: { keywordId: string; colorPopup: boolean } }) => {
      const { keywordId, colorPopup } = payload;
      const objIndex = state.bpartKeywordList.findIndex((item) => item.id === keywordId);
      state.bpartKeywordList[objIndex].colorpopup = colorPopup;
    },
    // Bpart Tracked notes 팝업 열고닫기
    notesPopupHandler: (state, { payload }: { payload: { keywordId: string; notesPopup: boolean } }) => {
      const { keywordId, notesPopup } = payload;
      const objIndex = state.bpartKeywordList.findIndex((item) => item.id === keywordId);
      state.bpartKeywordList[objIndex].notesPopup = notesPopup;
    },
    // Bpart Tracked live 팝업 데이터 초기화 처리
    setInitialLivePopupData: (state, { payload }) => {
      state.livePopupData = null;
    },
    // Bpart Tracked suggest 팝업 데이터 초기화 처리
    setInitialSuggestPopupData: (state, { payload }) => {
      state.suggestPopupData = null;
      state.suggestPopupKeyword = null;
    },
    // ApartKeywordData 초기화 처리 (tab 변경시 마다 초기화 처리 후 해당 컴포넌트에서 요청 처리)
    setInitialApartKeywordList: (state, { payload }) => {
      state.apartkeywordList = null;
    },
    // BpartKeywordData 초기화 처리 (tab 변경시 마다 초기화 처리 후 해당 컴포넌트에서 요청 처리)
    setInitailBpartKeywordList: (state, { payload }) => {
      state.bpartKeywordList = null;
    },
  },
  extraReducers: {
    // ApartkeywordList 데이터 응답
    [fetchApartKeywordDataList.fulfilled.type]: (state, { payload }) => {
      state.apartkeywordList = payload;
    },
    // BpartKeywordList 데이터 응답
    [fetchBpartKeywordDataList.fulfilled.type]: (state, { payload, meta: { requestStatus } }) => {
      console.log('requestStatus: fulfilled', requestStatus, payload);
      state.bpartKeywordList = payload;
      state.currentBpartKeywordList = !!payload && payload.length > 0 ? payload.map((item) => item.id) : null;
      state.requestStatusBpartKeywordList = requestStatus;
    },
    [fetchBpartKeywordDataList.pending.type]: (state, { meta: { requestStatus } }) => {
      console.log('requestStatus: fetchBpartKeywordDataList', requestStatus);
      state.requestStatusBpartKeywordList = requestStatus;
    },
    [fetchBpartKeywordDataList.rejected.type]: (state, { meta: { requestStatus } }) => {
      console.log('requestStatus: rejected', requestStatus);
      state.requestStatusBpartKeywordList = requestStatus;
    },

    // ApartKeywordAddDel 데이터 응답 (추가 시 currentBpartKeywordList 넣기/ 제거 시 빼기)
    // BpartSuggestPopupAddDel 도 같이 써도 될 것 같아 보인다.
    [fetchApartReqAddDelKeyword.fulfilled.type]: (
      state,
      { payload }: { payload: { bpartData: any; params: IApartAddDelKeywordParams } }
    ) => {
      const { bpartData, params } = payload;
      // Apart Add/Del 아이콘 수정 처리를 위한 currentBpartKeywordList 처리
      const { reqType, keyword, keywordId } = params;
      if (reqType === 'add') {
        state.currentBpartKeywordList = state.currentBpartKeywordList.concat(keywordId);
      } else if (reqType === 'del') {
        state.currentBpartKeywordList = state.currentBpartKeywordList.filter((item) => item !== keywordId);
      }
      // Bpart 를 위한 처리
      state.bpartKeywordList = bpartData;
    },
    // ApartKeywordAddAll 데이터 응답
    [fetchApartReqAddAllKeyword.fulfilled.type]: (
      state,
      { payload }: { payload: { bpartData: any; params: IApartAddAllKeywordParams } }
    ) => {
      const { bpartData, params } = payload;
      // Apart AddAll 로 인한 아이콘 수정 처리를 위한 currentBpartKeywordList 처리
      const { reqKeywordObjArr } = params;
      state.currentBpartKeywordList = Array.from(
        new Set(state.currentBpartKeywordList.concat(reqKeywordObjArr.map((item) => item.id)))
      );
      // Bpart 를 위한 처리
      state.bpartKeywordList = bpartData;
    },
    // BpartKeywordColor change
    [fetchBpartReqColorSelect.fulfilled.type]: (state, { payload }: { payload: IBpartTrackedColorParams }) => {
      const { keywordId, color } = payload;
      const objIndex = state.bpartKeywordList.findIndex((item) => item.id === keywordId);
      state.bpartKeywordList[objIndex].color = color;
      state.bpartKeywordList[objIndex].colorpopup = false;
    },
    // BpartKeywordAlarm change
    [fetchBpartReqAlarm.fulfilled.type]: (state, { payload }: { payload: IBpartTrackedAlarmParams }) => {
      const { keywordId, alert } = payload;
      const objIndex = state.bpartKeywordList.findIndex((item) => item.id === keywordId);
      state.bpartKeywordList[objIndex].alert = alert;
    },
    // BpartKeywordDelete Change TODO: keyword 1개만 삭제하는 부분.. 추후 삭제 예정
    [fetchBpartReqDelete.fulfilled.type]: (state, { payload }: { payload: IBpartTrackedDeleteParams }) => {
      const { keywordId } = payload;
      // 데이터 하나 빼기
      state.bpartKeywordList = state.bpartKeywordList.filter((item) => item.id !== keywordId);
      // 기존 Bpart 키워드ID 리스트에서 해당 keywordId 도 빼기
      state.currentBpartKeywordList = state.currentBpartKeywordList.filter((item) => item !== keywordId);
    },
    // BpartKeywordDelete Change 다중 keyword 삭제하는 부분
    [fetchBpartReqKeywordDelete.fulfilled.type]: (state, { payload }: { payload: IBpartKeywordDelParams }) => {
      const { keywordIdArr } = payload;
      keywordIdArr.forEach((keywordId) => {
        state.bpartKeywordList = state.bpartKeywordList.filter((item) => item.id !== keywordId);
        state.currentBpartKeywordList = state.currentBpartKeywordList.filter((item) => item !== keywordId);
      });
    },
    // BpartKeywordLivePopup 처리
    [fetchBpartReqLivePopup.fulfilled.type]: (
      state,
      { payload }: { payload: { liveData: any; params: IBpartTrackedLiveSuggestParams } }
    ) => {
      const { liveData, params } = payload;
      state.livePopupData = liveData;
    },
    // BpartKeywordSuggestPopup 처리
    [fetchBpartReqSuggestPopup.fulfilled.type]: (
      state,
      { payload }: { payload: { suggestData: IBpartTrackedSuggestPopupData; params: IBpartTrackedLiveSuggestParams } }
    ) => {
      const { suggestData, params } = payload;
      const { relatedKeywords, searchSuggestions } = suggestData;
      // useParams 를 사용하여 queryString 을 통해 넘기기 위한 keyword 설정
      state.suggestPopupKeyword = params.keyword;
      state.suggestPopupData = {
        relatedKeywords: relatedKeywords.sort((a, b) => b.searchscore - a.searchscore).slice(0, 10),
        searchSuggestions: searchSuggestions.sort((a, b) => b.searchscore - a.searchscore).slice(0, 10),
      };
    },
    // BpartKeywordSuggestPopup Add/Del 처리
    [fetchBpartSuggestPopupAddDel.fulfilled.type]: (
      state,
      { payload }: { payload: IBpartTrackedSuggestPopupAddDelParams }
    ) => {
      const { keywordId, reqType, categoryType } = payload;
      if (reqType === 'add') {
        state.currentBpartKeywordList = state.currentBpartKeywordList.concat(keywordId);
      } else if (reqType === 'del') {
        state.currentBpartKeywordList = state.currentBpartKeywordList.filter((item) => item !== keywordId);
      }
    },
  },
});

export const {
  setCurrentBpartTabType,
  colorPopupHandler,
  setInitialLivePopupData,
  setInitialSuggestPopupData,
  setInitialApartKeywordList,
  setInitailBpartKeywordList,
  notesPopupHandler,
} = keywordTrackingSlice.actions;
export const keywordTrackingSliceName = keywordTrackingSlice.name;
export default keywordTrackingSlice.reducer;
